import { Box, Button, keyframes, Typography } from "@mui/material";
import { formatDate, formatTime } from "@repo/utils/date";
import React from "react";
import { useFindDeliveryLog } from "@/features/delivery/hooks/use-delivery-actions";

const ERROR_PREFIX = "[ERREUR] ";
const ping = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(211, 47, 47, 0); }
  100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); }
`;

type DeliveryLogEntry = {
  id: string;
  log: string;
  createdAt: string;
  deliveryId: number;
};

type TDeliveryLogs = {
  deliveryId: number;
};

const PAGE_SIZE = 10;
const BUTTON_SX = { textTransform: "none", fontWeight: 600, fontSize: "0.75rem" } as const;

const formatTimestamp = (value: string) => ({
  time: formatTime(value),
  date: formatDate(value),
});

function LogItem({ log, isLast }: { log: DeliveryLogEntry; isLast: boolean }) {
  const { time, date } = formatTimestamp(log.createdAt);
  const isError = log.log.startsWith(ERROR_PREFIX);
  const displayLog = isError ? log.log.slice(ERROR_PREFIX.length) : log.log;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "85px 24px 1fr", sm: "95px 24px 1fr" },
      }}
    >
      <Box sx={{ textAlign: "right", pr: 2 }}>
        <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
          {time}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
          {date}
        </Typography>
      </Box>

      <Box sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
        {!isLast && (
          <Box
            sx={{ position: "absolute", top: 12, bottom: -28, width: "1px", bgcolor: "divider" }}
          />
        )}
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: isError ? "error.main" : "primary.main",
            mt: "6px",
            animation: isError ? `${ping} 1.5s infinite` : undefined,
          }}
        />
      </Box>

      <Box sx={{ pl: 1, pb: isLast ? 0 : 3.5 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#f0f0f0",
            border: isError ? 1 : 0,
            borderColor: "error.light",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.65,
              color: isError ? "error.dark" : "text.primary",
            }}
          >
            {displayLog}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

type PaginationBarProps = {
  rangeLabel: string;
  canGoBack: boolean;
  canGoForward: boolean;
  onPrevious: () => void;
  onNext: () => void;
};

function PaginationBar({
  rangeLabel,
  canGoBack,
  canGoForward,
  onPrevious,
  onNext,
}: PaginationBarProps) {
  return (
    <Box
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
        px: 3,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Button size="small" onClick={onPrevious} disabled={!canGoBack} sx={BUTTON_SX}>
        Prev
      </Button>
      <Typography variant="caption" color="text.secondary">
        {rangeLabel}
      </Typography>
      <Button size="small" onClick={onNext} disabled={!canGoForward} sx={BUTTON_SX}>
        Next
      </Button>
    </Box>
  );
}

function DeliveryLogs({ deliveryId }: TDeliveryLogs) {
  const [page, setPage] = React.useState(1);
  const { data } = useFindDeliveryLog({ deliveryId, page, pageSize: PAGE_SIZE });
  const logs = data?.logs ?? [];
  const totalLogs = data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalLogs / PAGE_SIZE);
  const canGoBack = page > 1;
  const canGoForward = page < totalPages;

  const pageStart = totalLogs === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, totalLogs);
  const rangeLabel = totalLogs === 0 ? "0–0 of 0" : `${pageStart}–${pageEnd} of ${totalLogs}`;

  return (
    <Box>
      <Box sx={{ px: 3, py: 3 }}>
        {logs.length > 0 ? (
          logs.map((log, index) => (
            <LogItem key={log.id} log={log} isLast={index === logs.length - 1} />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={6}>
            No history entries recorded.
          </Typography>
        )}
      </Box>

      <PaginationBar
        rangeLabel={rangeLabel}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onPrevious={() => canGoBack && setPage((currentPage) => currentPage - 1)}
        onNext={() => canGoForward && setPage((currentPage) => currentPage + 1)}
      />
    </Box>
  );
}

export default DeliveryLogs;
